import { useState, useEffect, useCallback } from 'react';
import { parseISO, format } from 'date-fns';
import { HardHat, Clock, MapPin, Send, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER } from './theme';
import { DiaryPhotoLightbox, bcPhotoItems } from './DiaryPhotoLightbox';

const META = { fg: '#0D9488', bg: '#CCFBF1' };

function Thumb({ api, captureId, photo, onOpen }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj; let active = true;
    api.get(`/bc/captures/${captureId}/photo/${photo.id}?thumb=1`, { responseType: 'blob' })
      .then(r => { if (active) { obj = URL.createObjectURL(r.data); setUrl(obj); } }).catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [api, captureId, photo.id]);
  return (
    <button type="button" onClick={() => onOpen(photo)}
      className="w-14 h-14 rounded-md overflow-hidden flex-shrink-0 relative flex items-center justify-center"
      style={{ border: `1px solid ${PAPER.border}`, background: PAPER.paperMuted }}
      data-testid={`bc-diary-thumb-${photo.id}`}>
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <Loader2 className="w-4 h-4 animate-spin" style={{ color: PAPER.hint }} />}
      {photo.sent && <span className="absolute bottom-0 left-0 right-0 text-[8px] text-center font-bold bg-emerald-600 text-white">SENT</span>}
    </button>
  );
}

/**
 * Building Control capture row in the diary timeline (BC-1).
 * Hydrates from /bc/captures/{id}; shows stage, GPS, status and photo
 * thumbnails, plus a "Send to Building Control" action for surveyors/admins
 * on a saved (not-yet-sent) capture.
 */
export function BCEntryCard({ entry, canSend, isAdmin, onChanged }) {
  const { api } = useAuth();
  const captureId = entry.payload?.bc_capture_id;
  const [cap, setCap] = useState(null);
  const [busy, setBusy] = useState(false);
  // Index into cap.photos for the full-screen viewer, or null.
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    if (!captureId) return;
    try { const r = await api.get(`/bc/captures/${captureId}`); setCap(r.data); } catch { /* non-blocking */ }
  }, [api, captureId]);
  useEffect(() => { load(); }, [load]);

  const created = entry.created_at ? parseISO(entry.created_at) : null;
  const timeLabel = created ? format(created, 'HH:mm') : '';
  const isSend = cap ? cap.kind === 'send' : entry.payload?.sent;
  const stage = cap?.stage || entry.payload?.stage;

  const doSend = async () => {
    setBusy(true);
    try {
      await api.post(`/bc/captures/${captureId}/send`, {});
      toast.success('Sent to Building Control (queued if email is not live yet)');
      onChanged?.();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm('Delete this Building Control capture? This permanently removes its photos and this diary entry. Anything already filed to SharePoint stays there.')) return;
    setBusy(true);
    try { await api.delete(`/bc/captures/${captureId}`); toast.success('Capture deleted'); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(false); }
  };

  const statusBadge = () => {
    if (isSend) {
      const map = {
        sent: ['#065F46', '#D1FAE5', 'Sent'],
        queued: ['#92400E', '#FEF3C7', 'Queued'],
        failed: ['#B91C1C', '#FEE2E2', 'Failed'],
        discarded: ['#3F3F46', '#F4F4F5', 'Discarded'],
      };
      const [fg, bg, label] = map[cap?.delivery] || map.queued;
      return <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: bg, color: fg }} data-testid={`bc-diary-status-${captureId}`}>{label}</span>;
    }
    return <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: PAPER.paperMuted, color: PAPER.inkSoft }} data-testid={`bc-diary-status-${captureId}`}>Saved</span>;
  };

  return (
    <div className="rounded-xl px-3.5 py-3 flex gap-3" style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }} data-testid={`timeline-entry-${entry.id}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: META.bg, color: META.fg }}>
        <HardHat className="w-5 h-5" strokeWidth={2.25} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: META.fg }}>Building Control</span>
          {statusBadge()}
          {cap?.in_error && <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#FEE2E2', color: '#B91C1C' }}>In error</span>}
          <span className="ml-auto text-[11px] flex items-center gap-1" style={{ color: PAPER.muted }}><Clock className="w-3 h-3" />{timeLabel}</span>
          {isAdmin && (
            <button type="button" onClick={doDelete} disabled={busy} title="Delete capture"
              className="p-1 rounded hover:bg-black/5 disabled:opacity-50" style={{ color: '#B91C1C' }}
              data-testid={`bc-diary-delete-${captureId}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="text-[15px] font-semibold mt-0.5" style={{ color: PAPER.ink }}>{stage || 'Inspection'}</div>
        {cap?.note && <div className="text-[13px] mt-0.5" style={{ color: PAPER.inkSoft }}>{cap.note}</div>}
        {cap && (
          <div className="text-[11px] mt-1 flex items-center gap-1" style={{ color: PAPER.muted }}>
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{cap.gps?.unavailable ? 'GPS unavailable' : (cap.gps?.address || (cap.gps?.lat != null ? `${cap.gps.lat.toFixed(5)}, ${cap.gps.lng.toFixed(5)}` : 'GPS —'))}</span>
          </div>
        )}
        {cap?.photos?.length > 0 && (
          <div className="mt-2 flex gap-1.5 flex-wrap">
            {cap.photos.slice(0, 6).map((p, i) => <Thumb key={p.id} api={api} captureId={captureId} photo={p} onOpen={() => setLightbox(i)} />)}
            {cap.photos.length > 6 && (
              <button type="button" onClick={() => setLightbox(6)} className="w-14 h-14 rounded-md flex items-center justify-center text-[11px] font-semibold" style={{ background: PAPER.paperMuted, color: PAPER.inkSoft, border: `1px solid ${PAPER.border}` }} data-testid={`bc-diary-more-${captureId}`}>+{cap.photos.length - 6}</button>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 mt-2">
          <span className="text-[11px]" style={{ color: PAPER.muted }}>{entry.author_name || 'Someone'}</span>
          {canSend && cap && cap.kind === 'save' && (
            <button type="button"
              onClick={cap.project_has_bc_contact ? doSend : undefined}
              disabled={busy || !cap.project_has_bc_contact}
              title={cap.project_has_bc_contact ? 'Send to Building Control' : 'Assign a Building Control contact to this project (admin) to enable sending'}
              className="ml-auto text-[12px] font-semibold px-2.5 py-1.5 rounded-md flex items-center gap-1.5 disabled:cursor-not-allowed"
              style={cap.project_has_bc_contact ? { background: '#065F46', color: '#fff' } : { background: PAPER.paperMuted, color: PAPER.hint }}
              data-testid={`bc-diary-send-${captureId}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {cap.project_has_bc_contact ? 'Send to Building Control' : 'No BC contact'}
            </button>
          )}
        </div>
        {canSend && cap && cap.kind === 'save' && !cap.project_has_bc_contact && (
          <p className="text-[11px] mt-1" style={{ color: PAPER.muted }} data-testid={`bc-diary-send-hint-${captureId}`}>
            Ask an admin to assign a Building Control contact to this project to enable sending.
          </p>
        )}
      </div>

      {lightbox !== null && cap?.photos?.length > 0 && (
        <DiaryPhotoLightbox
          items={bcPhotoItems(captureId, cap.photos)}
          index={Math.min(lightbox, cap.photos.length - 1)}
          title={`Building Control — ${stage || 'photos'}`}
          onIndexChange={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

export default BCEntryCard;
